import {
  Button,
  FormCheckbox,
  FormInput,
  FormPrice,
  FormRoot,
  FormSelect,
  Modal,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import {
  DEFAULT_SHOP_CURRENCY,
  isShopCurrency,
  type ShopCurrency,
} from "@/shared/lib/currency";
import { FieldHelpHint } from "@/shared/ui/FieldHelpHint";
import type { ShopProductSkuListItem } from "../api/client";

const CURRENCY_OPTIONS = [
  { value: "LAK", label: "LAK" },
  { value: "THB", label: "THB" },
  { value: "USD", label: "USD" },
];

function currencySymbol(currency: ShopCurrency): string {
  if (currency === "THB") return "฿";
  if (currency === "USD") return "$";
  return "K";
}

const EditSkuSchema = z.object({
  sku: z.string().min(1, "ຕ້ອງໃສ່ລະຫັດ SKU"),
  barcode: z.string().optional().or(z.literal("")),
  price: z.coerce.number().min(0, "ລາຄາຕ້ອງບໍ່ຕ່ຳກວ່າ 0"),
  cost: z.union([z.coerce.number().min(0), z.literal("")]).optional(),
  currency: z.enum(["LAK", "THB", "USD"]),
  isActive: z.boolean(),
});

type EditSkuValues = z.infer<typeof EditSkuSchema>;

type EditShopProductSkuModalProps = {
  open: boolean;
  row: ShopProductSkuListItem | null;
  onClose: () => void;
  onSubmit: (body: {
    sku: string;
    barcode: string | null;
    price: number;
    cost: number | null;
    currency: "LAK" | "THB" | "USD";
    isActive: boolean;
  }) => Promise<void>;
  shopDefaultCurrency?: string;
  enforceSingleShopSkuCurrency?: boolean;
};

export function EditShopProductSkuModal({
  open,
  row,
  onClose,
  onSubmit,
  shopDefaultCurrency,
  enforceSingleShopSkuCurrency = false,
}: EditShopProductSkuModalProps) {
  const rawDefault = String(shopDefaultCurrency ?? "");
  const lockedCurrency: ShopCurrency = isShopCurrency(rawDefault)
    ? rawDefault
    : DEFAULT_SHOP_CURRENCY;
  const skuCurrencyLocked = enforceSingleShopSkuCurrency;
  const priceSymbol = currencySymbol(
    skuCurrencyLocked
      ? lockedCurrency
      : isShopCurrency(String(row?.currency ?? ""))
        ? (row!.currency as ShopCurrency)
        : lockedCurrency,
  );

  const methods = RHF.useForm<EditSkuValues>({
    resolver: zodResolver(EditSkuSchema),
    defaultValues: {
      sku: "",
      barcode: "",
      price: 0,
      cost: undefined,
      currency: lockedCurrency,
      isActive: true,
    },
  });

  useEffect(() => {
    if (row && open) {
      const rowCur = isShopCurrency(String(row.currency ?? ""))
        ? (row.currency as ShopCurrency)
        : DEFAULT_SHOP_CURRENCY;
      methods.reset({
        sku: row.sku,
        barcode: row.barcode ?? "",
        price: row.price,
        cost: row.cost ?? undefined,
        currency: skuCurrencyLocked ? lockedCurrency : rowCur,
        isActive: row.isActive,
      });
    }
  }, [row, open, methods, skuCurrencyLocked, lockedCurrency]);

  const handleSubmit = (vals: EditSkuValues) => {
    if (!row) return;
    onSubmit({
      sku: vals.sku.trim(),
      barcode: vals.barcode?.trim() || null,
      price: vals.price,
      cost:
        vals.cost !== undefined && vals.cost !== null && vals.cost !== ""
          ? Number(vals.cost)
          : null,
      currency: skuCurrencyLocked ? lockedCurrency : vals.currency,
      isActive: vals.isActive,
    });
    onClose();
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => (!o ? onClose() : undefined)}
      title="ແກ້ໄຂລາຄາ / SKU ຕໍ່ຮ້ານ"
      description={row ? `ສິນຄ້າ: ${row.productName}` : undefined}
      size="md"
    >
      <div className="space-y-4">
        {row && (
          <FormRoot<EditSkuValues>
            methods={methods}
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <FormInput
              name="sku"
              label={
                <FieldHelpHint label="SKU ຂອງຮ້ານ">
                  ລະຫັດສິນຄ້າຂອງຮ້ານນີ້ — ໃຊ້ໃນ POS / ສະແກນ / ຄົ້ນຫາ.
                  ແຕກຕ່າງຈາກ OEM Part No ຂອງ catalog.
                </FieldHelpHint>
              }
              placeholder="ລະຫັດ SKU"
              requiredMark
            />
            <FormInput name="barcode" label="Barcode" placeholder="Barcode" />
            <FormPrice
              name="price"
              label="ລາຄາ"
              requiredMark
              currencySymbol={priceSymbol}
              placeholder="0"
            />
            <FormPrice
              name="cost"
              label="ຕົ້ນທຶນ"
              currencySymbol={priceSymbol}
              placeholder="—"
            />
            {skuCurrencyLocked ? (
              <div className="space-y-2">
                <p className="font-medium text-sm">ສະກຸນເງິນ</p>
                <p className="rounded-md border border-border bg-muted/30 px-3 py-2.5 font-mono text-sm">
                  {lockedCurrency}
                </p>
                <p className="text-muted-foreground text-xs leading-snug">
                  ຮ້ານບັງຄັບໃຫ້ SKU ໃຊ້ສະກຸນດຽວກັບຕັ້ງຄ່າຮ້ານ
                </p>
                <input type="hidden" {...methods.register("currency")} />
              </div>
            ) : (
              <FormSelect
                name="currency"
                label="ສະກຸນເງິນ"
                options={CURRENCY_OPTIONS}
              />
            )}
            <FormCheckbox name="isActive" label="ໃຊ້ງານ (ເປີດຂາຍ)" />

            <div className="flex justify-end gap-2 border-border/80 border-t pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                ຍົກເລີກ
              </Button>
              <Button
                type="submit"
                className="bg-[#F26522] hover:bg-[#F26522]/90"
              >
                ບັນທຶກ
              </Button>
            </div>
          </FormRoot>
        )}
      </div>
    </Modal>
  );
}
